// Named sidebar data providers for the Workspace app.
//
// One provider, for the Master section: its rows ARE the workspace's master
// lists, so they can only come from the backend — and each row's count is a
// cross-field tally (rows belonging to that category) that a declarative
// `{ api, map }` can't express.

import { useAuthStore } from '@clarix/auth-store';
import { useWorkspaceMasters } from '@clarix/services-master';
import type { RowConfig, SidebarProviderResult } from '@clarix/shared-types';

/**
 * Per-category icon for the workspace master lists, keyed by the backend
 * category code. Mirrors the Projects app's Masters icon language (Building =
 * departments, Briefcase = designations, …); an unknown category falls back to
 * a generic list glyph.
 */
const MASTER_CATEGORY_ICON: Record<string, string> = {
  designation: 'Briefcase',
  department: 'Building2',
  employee_level: 'GraduationCap',
  employment_type: 'BadgeCheck',
  work_location: 'MapPin',
  holiday_region: 'CalendarDays',
  approval_status: 'CheckCircle2',
  skill: 'Lightbulb',
  user_status: 'UserCheck',
};

/** The workspace's master lists (departments, designations, …), each badged
 *  with how many entries it holds. */
export function useWorkspaceMastersProvider(): SidebarProviderResult {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const masters = useWorkspaceMasters(slug);

  const categories = masters.data?.categories ?? [];
  const rows = masters.data?.rows ?? [];

  return {
    rows: categories.map<RowConfig>((c) => ({
      id: c.category,
      label: c.label,
      icon: MASTER_CATEGORY_ICON[c.category] ?? 'List',
      toParam: { pathname: '/workspace/masters', param: 'category', value: c.category },
      // A list's size, not an alert — rendered as a muted number.
      badge: { kind: 'plain', value: rows.filter((r) => r.category === c.category).length },
    })),
    isLoading: masters.isPending,
    isError: masters.isError,
    emptyText: 'No lists',
  };
}
